import React from "react";

/**
 * Renders aggregated responses for any challenge type.
 * Uses the type-specific data returned by GET /api/sessions/:id/results/:challenge_id
 */
export function ChallengeAggregations({ challenge, results, responded }) {
  if (!challenge || !results) return null;
  const ctype = challenge.type;

  if (ctype === "quick_decision" || ctype === "chat_simulation" || ctype === "vote") {
    return <ChoiceBars choices={challenge.choices || []} counts={results.counts_by_choice} responded={responded} />;
  }

  if (ctype === "multi_step") {
    return (
      <div className="space-y-4">
        {(challenge.steps || []).map((step) => {
          const stepCounts = results.per_step?.[step.id] || {};
          const stepTotal = Object.values(stepCounts).reduce((a, b) => a + b, 0);
          return (
            <div key={step.id} className="bg-slate-50 rounded-md p-3">
              <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">
                {step.prompt}
              </div>
              <ChoiceBars choices={step.choices || []} counts={stepCounts} responded={stepTotal} />
            </div>
          );
        })}
      </div>
    );
  }

  if (ctype === "feed_analysis") {
    const items = challenge.feed_items || [];
    return (
      <div className="space-y-2">
        {items.map((it) => {
          const stat = results.feed_items?.[it.id];
          const count = stat?.selected_count || 0;
          const pct = responded ? (count / responded) * 100 : 0;
          return (
            <div key={it.id} className={`rounded-md p-2 border ${it.risky ? "border-red-200 bg-red-50/50" : "border-slate-200"}`}>
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="font-bold truncate flex-1 mr-2">
                  {it.risky && <span className="text-red-600 mr-1">⚠</span>}
                  @{it.author}
                </span>
                <span className="text-xs text-slate-500 tabular-nums">{count} / {responded} označilo</span>
              </div>
              <div className="text-xs text-slate-600 truncate mb-1">{it.text}</div>
              <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div className={`h-full ${it.risky ? "bg-green-500" : "bg-amber-500"}`} style={{ width: `${pct}%` }} />
              </div>
            </div>
          );
        })}
        <div className="text-[11px] text-slate-500 mt-2">
          Zelená lišta = správně označeno (rizikové), oranžová = falešný poplach.
        </div>
      </div>
    );
  }

  if (ctype === "sequence") {
    const items = challenge.items || [];
    const correct = challenge.correct_order || [];
    const fullMatches = results.sequence_full_match_count || 0;
    return (
      <div className="space-y-2">
        <div className="text-xs text-slate-500">
          {fullMatches} žáků trefilo přesné správné pořadí.
        </div>
        {items.map((it) => {
          const avg = results.avg_positions?.[it.id];
          const correctIdx = correct.indexOf(it.id);
          return (
            <div key={it.id} className="flex items-center gap-2 text-sm">
              <div className="flex-1 truncate">
                <span className="font-bold">{it.label}</span>
              </div>
              <div className="text-xs tabular-nums text-slate-500 w-24 text-right">
                Ø pozice: <span className="font-bold text-slate-900">{avg ?? "—"}</span>
              </div>
              {correctIdx >= 0 && (
                <div className="text-[10px] uppercase tracking-wider text-green-700 bg-green-100 rounded-full px-2 py-0.5 w-16 text-center">
                  Správně {correctIdx + 1}.
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  if (ctype === "reflection") {
    const moodChoices = challenge.mood_choices || [];
    const moodCounts = results.mood_counts || {};
    return (
      <div className="space-y-3">
        {moodChoices.length > 0 && (
          <div>
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">Pocity</div>
            <div className="grid grid-cols-2 gap-2">
              {moodChoices.map((m) => {
                const count = moodCounts[m.id] || 0;
                return (
                  <div key={m.id} className="bg-slate-50 rounded-md p-2 flex items-center justify-between text-sm">
                    <span className="font-bold">{m.label}</span>
                    <span className="tabular-nums text-slate-500">{count}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {(results.reflections || []).length > 0 && (
          <div>
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">Odpovědi (anonymně)</div>
            <div className="space-y-1 max-h-64 overflow-y-auto">
              {results.reflections.map((r, i) => (
                <div key={i} className="bg-slate-50 rounded-md p-2 text-sm italic text-slate-700">
                  „{r.text}"
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Fallback
  return <ChoiceBars choices={challenge.choices || []} counts={results.counts_by_choice} responded={responded} />;
}

function ChoiceBars({ choices, counts, responded }) {
  if (!choices || choices.length === 0) {
    return <div className="text-xs text-slate-400">Žádné volby k zobrazení.</div>;
  }
  return (
    <div className="space-y-2">
      {choices.map((c) => {
        const count = counts?.[c.id] || 0;
        const pct = responded ? (count / responded) * 100 : 0;
        return (
          <div key={c.id}>
            <div className="flex justify-between text-sm mb-1 gap-2">
              <span className="text-slate-700 truncate">{c.label}</span>
              <span className="font-bold tabular-nums flex-shrink-0">{count}</span>
            </div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full bg-blue-600 transition-all" style={{ width: `${pct}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}
